import React, { useMemo } from 'react'
import type { SmpteController } from '@/organisms/AngelPlayer/BitmovinPlayer/types'
import { HlsCenterPlayOverlay } from './HlsCenterPlayOverlay'
import { HlsControlBar } from './HlsControlBar'
import { HlsPlayerUIProvider } from './HlsPlayerUIContext'
import { useControlBarVisibility } from './useControlBarVisibility'
import { useVideoState } from './useVideoState'

interface HlsPlayerUIProps {
  videoRef: React.RefObject<HTMLVideoElement | null>
  controller: SmpteController | null
  containerRef: React.RefObject<HTMLElement | null>
}

/**
 * Top-level UI overlay for the HLS.js player.
 * Sets up the HlsPlayerUIContext (video state + controller) and renders:
 * - Center play/pause overlay (click-to-play on video area)
 * - Bottom control bar with all playback controls
 */
export const HlsPlayerUI: React.FC<HlsPlayerUIProps> = ({ videoRef, controller, containerRef }) => {
  const videoState = useVideoState(videoRef)
  const { visible, onControlBarInteraction } = useControlBarVisibility(containerRef, videoState.playing)

  const contextValue = useMemo(
    () => ({
      currentTime: videoState.currentTime,
      duration: videoState.duration,
      buffered: videoState.buffered,
      playing: videoState.playing,
      volume: videoState.volume,
      muted: videoState.muted,
      controller,
    }),
    [videoState, controller],
  )

  return (
    <HlsPlayerUIProvider value={contextValue}>
      <HlsCenterPlayOverlay visible={visible} />
      <HlsControlBar visible={visible} onInteraction={onControlBarInteraction} />
    </HlsPlayerUIProvider>
  )
}
